import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { User } from '@wranglr/shared';
import * as api from '../services/api';

interface AuthContextType {
    user: Omit<User, 'password_hash'> | null;
    token: string | null;
    loading: boolean;
    login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
    register: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
    logout: () => void;
    refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<Omit<User, 'password_hash'> | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const initAuth = async () => {
            // Check for existing token on mount
            const storedToken = localStorage.getItem('token');
            const storedUser = localStorage.getItem('user');

            if (storedToken) {
                setToken(storedToken);
                // Optimistically set user from storage if available
                if (storedUser) {
                    try {
                        setUser(JSON.parse(storedUser));
                    } catch {
                        // Invalid JSON
                    }
                }

                // Fetch fresh user data from API
                try {
                    const response = await api.getMe();
                    if (response.success && response.data) {
                        setUser(response.data.user);
                        localStorage.setItem('user', JSON.stringify(response.data.user));
                    } else {
                        // Token might be invalid/expired
                        // Optional: logout() if 401, but for now just don't update
                    }
                } catch (err) {
                    console.error('Failed to refresh user session', err);
                }
            }
            setLoading(false);
        };

        initAuth();
    }, []);

    const login = async (email: string, password: string) => {
        const response = await api.login(email, password);

        if (response.success && response.data) {
            setToken(response.data.token);
            setUser(response.data.user);
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('user', JSON.stringify(response.data.user));
            return { success: true };
        }

        return { success: false, error: response.error || 'Login failed' };
    };

    const register = async (email: string, password: string) => {
        const response = await api.register(email, password);

        if (response.success && response.data) {
            setToken(response.data.token);
            setUser(response.data.user);
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('user', JSON.stringify(response.data.user));
            return { success: true };
        }

        return { success: false, error: response.error || 'Registration failed' };
    };

    const logout = () => {
        setToken(null);
        setUser(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    };

    const refreshUser = async () => {
        try {
            const response = await api.getMe();
            if (response.success && response.data) {
                setUser(response.data.user);
                localStorage.setItem('user', JSON.stringify(response.data.user));
            }
        } catch (err) {
            console.error('Failed to refresh user', err);
        }
    };

    return (
        <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshUser }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export function useOptionalAuth() {
    return useContext(AuthContext);
}
